import Accordion from '../../../Accordion/Accordion';
import List from '../../../List/List';
import StyledStyle from './styles';
import Props, {FormattedSelection, ListNodeWithSublist, ListProps, OptionProps} from './typings';

const list: Readonly<ListProps> = {
  type: 'radio',
  style: 'text',
  options: [
    {
      label: 'all',
      value: 'all',
    },
    {
      label: 'abstract',
      value: 'abstract',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'organic', value: 'abstract-organic'},
          {label: 'geometric', value: 'abstract-geometric'},
        ],
      },
    },
    {
      label: 'collage',
      value: 'collage',
    },
    {
      label: 'photorealistic',
      value: 'photorealistic',
    },
    {
      label: 'impressionistic',
      value: 'impressionistic',
    },
    {
      label: 'expressive and gestural',
      value: 'expressive-and-gestural',
    },
    {
      label: 'graphic, illustrative and typographic',
      value: 'graphic-illustrative-and-typographic',
      sublist: {
        type: 'radio',
        style: 'button',
        options: [
          {label: 'all', value: 'all'},
          {label: 'cartoon', value: 'graphic-illustrative-and-typographic-cartoon'},
          {label: 'graphic', value: 'graphic-illustrative-and-typographic-graphic'},
          {label: 'illustrative', value: 'graphic-illustrative-and-typographic-illustrative'},
          {label: 'typographic', value: 'graphic-illustrative-and-typographic-typographic'},
        ],
      },
    },
    {
      label: 'surrealistic',
      value: 'surrealistic',
    },
    {
      label: 'urban and pop',
      value: 'urban-and-pop',
    },
    {
      label: 'naive',
      value: 'naive',
    },
    {
      label: 'unspecified',
      value: 'unspecified',
    },
  ],
};

const hasSublist = (option: OptionProps): option is ListNodeWithSublist => {
  return typeof option.sublist !== 'undefined';
};

/**
 * Output should be in format:
 * [{value: 'option-value', subcategories: [{value: 'subcategory-value'}]}]
 * @param {string} selected value for current selection
 * @return {Array}
 */
const inputFormatter = (selected: string[]): FormattedSelection[] => {
  const topLevelNodes: string[] = list.options.map((style) => style.value);
  const nodesWithSublist: ListNodeWithSublist[] = list.options.filter(hasSublist);
  const formattedSelection: FormattedSelection[] = [];
  for (const selection of selected) {
    if (topLevelNodes.includes(selection)) {
      formattedSelection.push({value: selection});
      break;
    }
    for (const style of nodesWithSublist) {
      if (style.sublist.options.map((substyle) => substyle.value).includes(selection)) {
        const existingNode = formattedSelection.find(
          (addedStyle) => addedStyle.value === style.value,
        );
        if (existingNode) {
          const subcategories: any[] = existingNode.subcategories ? existingNode.subcategories : [];
          existingNode.subcategories = [...subcategories, {value: selection}];
          formattedSelection.push(existingNode);
        } else {
          formattedSelection.push({value: style.value, subcategories: [{value: selection}]});
        }
        break;
      }
    }
  }
  return formattedSelection;
};

export const getStyleTitle = (selected) => {
  const titleArray: string[] = [];

  if (!!selected.length) {
    const lookupValue = inputFormatter(selected)[0].value;
    const selectedOption = list.options.find((option) => option.value === lookupValue);
    if (selectedOption) titleArray.push(selectedOption.label);
  }
  return {label: titleArray, count: selected.length};
};

const Style = ({selected, styleCallback, collapsible = false, defaultCollapsed = false}: Props) => {
  const title = 'Style';
  const subtitle = 'Select your preferred style.';

  const getStyleValue = (styles) => {
    for (const style of styles) {
      if (style.subcategories?.length > 0) {
        return getStyleValue(style.subcategories);
      } else {
        return style.value;
      }
    }
  };

  const outputFormatter = (data) => {
    const response = getStyleValue(data);
    response ? styleCallback([response]) : styleCallback([]);
  };

  const fragmentBody = () => (
    <List
      options={list.options}
      type={list.type}
      style={list.style}
      group="style"
      selected={inputFormatter(selected)}
      callback={outputFormatter}
    />
  );

  const collapsibleFragment = () => {
    return (
      <Accordion title={title} subtitle={subtitle} defaultCollapsed={defaultCollapsed}>
        {fragmentBody()}
      </Accordion>
    );
  };

  const fragment = () => (
    <StyledStyle>
      <h4>{title}</h4>

      <p>{subtitle}</p>

      {fragmentBody()}
    </StyledStyle>
  );

  return collapsible === true ? collapsibleFragment() : fragment();
};

export default Style;
